import React, { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { describeOtpError, isOtpComplete, normalizeOtpInput } from '@/lib/emailOtp'

// Supabase refuses a new OTP email for the same user within 60 seconds.
const RESEND_COOLDOWN_SECONDS = 60

type OtpResult = { error: { code?: string; status?: number } | null }

interface OtpCodeFormProps {
    // The parent moves on (next step, redirect) inside onVerify when the code is accepted.
    onVerify: (code: string) => Promise<OtpResult>
    onResend: () => Promise<OtpResult>
    submitLabel: string
    // Extra links shown under the resend button.
    children?: React.ReactNode
}

export const OtpCodeForm = ({ onVerify, onResend, submitLabel, children }: OtpCodeFormProps) => {
    const [code, setCode] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState('')
    const [notice, setNotice] = useState('')
    const [resendCooldown, setResendCooldown] = useState(RESEND_COOLDOWN_SECONDS)

    useEffect(() => {
        if (resendCooldown <= 0) return
        const timer = setTimeout(() => setResendCooldown((seconds) => seconds - 1), 1000)
        return () => clearTimeout(timer)
    }, [resendCooldown])

    const run = async (action: () => Promise<OtpResult>): Promise<boolean> => {
        setError('')
        setNotice('')
        setIsLoading(true)
        try {
            const { error } = await action()
            if (error) {
                setError(describeOtpError(error))
                return false
            }
            return true
        } catch {
            setError(describeOtpError({}))
            return false
        } finally {
            setIsLoading(false)
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        await run(() => onVerify(code))
    }

    const handleResend = async () => {
        if (await run(onResend)) {
            setCode('')
            setNotice('Nouveau code envoyé. Le code précédent ne fonctionne plus.')
            setResendCooldown(RESEND_COOLDOWN_SECONDS)
        }
    }

    return (
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            <div>
                <label htmlFor="code" className="block text-sm font-medium text-gray-700">
                    Code reçu par e-mail
                </label>
                <input
                    id="code"
                    name="code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    required
                    autoFocus
                    className="mt-1 appearance-none rounded-md block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 text-center text-2xl font-mono tracking-[0.4em] focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="000000"
                    value={code}
                    onChange={(e) => setCode(normalizeOtpInput(e.target.value))}
                />
                <p className="mt-2 text-xs text-gray-500">
                    Pensez à vérifier vos spams. Seul le code du dernier e-mail reçu est valide.
                </p>
            </div>

            {error && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-800">
                    {error}
                </div>
            )}
            {notice && (
                <div className="bg-green-50 border border-green-200 rounded-md p-3 text-sm text-green-800">
                    {notice}
                </div>
            )}

            <Button type="submit" disabled={isLoading || !isOtpComplete(code)} className="w-full">
                {isLoading ? 'Vérification...' : submitLabel}
            </Button>

            <div className="flex flex-col items-center gap-2 text-sm">
                <Button
                    type="button"
                    variant="link"
                    disabled={isLoading || resendCooldown > 0}
                    onClick={handleResend}
                >
                    {resendCooldown > 0 ? `Renvoyer le code (${resendCooldown} s)` : 'Renvoyer le code'}
                </Button>
                {children}
            </div>
        </form>
    )
}
